/**
 * универсальный хук для понимания состояния скролла страницы.
 * производительность:
 *  - пассивные слушатели scroll/resize
 *  - троттлинг через requestAnimationFrame (1 рендер максимум на кадр)
 *  - обновление состояния ТОЛЬКО при реальных изменениях (порог delta и проверка флагов)
 * возможности:
 *  - isScrolled (с порогом threshold)
 *  - направление скролла (up/down/none)
 *  - isAtTop / isAtBottom
 *  - scrollY / scrollX
 * SSR-safe.
 */

import { useEffect, useRef, useState, useMemo } from 'react';

export type ScrollDirection = 'up' | 'down' | 'none';

export interface UseScrollInfoOptions {
    threshold?: number; // порог в пикселях, после которого считаем, что страница проскролена (по умолчанию 0).
    delta?: number; // минимальное изменение координаты (в пикселях, по умолчанию 2), чтобы обновить состояние. снижает количество ререндеров.
    enabled?: boolean; // включать/выключать слежение (по умолчанию true).
}

export interface ScrollInfo {
    x: number;
    y: number;
    directionY: ScrollDirection;
    isScrolled: boolean;
    isAtTop: boolean;
    isAtBottom: boolean;
}

function getWin(): Window | null {
    return typeof window !== 'undefined' ? window : null;
}

function getDoc(): Document | null {
    return typeof document !== 'undefined' ? document : null;
}

function readScrollX(win: Window) {
    return win.pageXOffset ?? win.scrollX ?? 0;
}
function readScrollY(win: Window) {
    return win.pageYOffset ?? win.scrollY ?? 0;
}

function readIsAtBottom(win: Window, doc: Document) {
    const el = doc.documentElement;
    const scrollHeight = el.scrollHeight;
    const viewport = win.innerHeight;
    const y = readScrollY(win);
    return y + viewport >= scrollHeight - 1;
}

function computeInfo(win: Window, doc: Document, prevY: number, threshold: number): ScrollInfo {
    const x = readScrollX(win);
    const y = readScrollY(win);
    const dy = y - prevY;
    const directionY: ScrollDirection = dy > 0 ? 'down' : dy < 0 ? 'up' : 'none';
    const isAtTop = y <= 0;
    const isAtBottom = readIsAtBottom(win, doc);
    const isScrolled = y > threshold;
    return { x, y, directionY, isScrolled, isAtTop, isAtBottom };
}

export function useScrollInfo(options: UseScrollInfoOptions = {}): ScrollInfo {
    const win = getWin();
    const doc = getDoc();
    const { threshold = 0, delta = 2, enabled = true } = options;
    const initial: ScrollInfo = useMemo(() => {
        if (!win || !doc) {
            return {
                x: 0,
                y: 0,
                directionY: 'none',
                isScrolled: false,
                isAtTop: true,
                isAtBottom: false,
            };
        }
        return computeInfo(win, doc, 0, threshold);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [state, setState] = useState<ScrollInfo>(initial);
    const prevYRef = useRef<number>(initial.y);
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
        if (!enabled || !win || !doc) return;
        const onScrollOrResize = () => {
            if (rafRef.current != null) return;
            rafRef.current = win.requestAnimationFrame(() => {
                rafRef.current = null;
                const prevY = prevYRef.current;
                const next = computeInfo(win, doc, prevY, threshold);
                prevYRef.current = next.y;
                const yChangedEnough = Math.abs(next.y - state.y) >= delta;
                const binaryChanged =
                    next.isScrolled !== state.isScrolled ||
                    next.isAtTop !== state.isAtTop ||
                    next.isAtBottom !== state.isAtBottom ||
                    next.directionY !== state.directionY;
                const xChangedEnough = Math.abs(next.x - state.x) >= delta;
                if (yChangedEnough || xChangedEnough || binaryChanged) {
                    setState(next);
                }
            });
        };
        onScrollOrResize();
        win.addEventListener('scroll', onScrollOrResize, { passive: true });
        win.addEventListener('resize', onScrollOrResize, { passive: true });
        return () => {
            if (rafRef.current != null) {
                win.cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            win.removeEventListener('scroll', onScrollOrResize);
            win.removeEventListener('resize', onScrollOrResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, threshold, doc, win, state.x, state.y, state.isScrolled, state.isAtTop, state.isAtBottom, state.directionY, delta]);
    return state;
}

export function useIsScrolled(threshold = 0, options: Omit<UseScrollInfoOptions, 'threshold'> = {}): boolean {
    const info = useScrollInfo({ threshold, ...options });
    return info.isScrolled;
}

export default useScrollInfo;

export function useIsBottomReached(thresholdPx = 0): boolean {
    const [isBottomReached, setIsBottomReached] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        let raf = 0;
        const getScrollEl = (): Element => {
            return document.scrollingElement || document.documentElement;
        };
        const compute = () => {
            const el = getScrollEl() as HTMLElement;
            const scrollTop = Math.max(0, el.scrollTop || 0);
            const clientHeight = el.clientHeight || window.innerHeight || 0;
            const scrollHeight = el.scrollHeight || 0;
            const reached = scrollTop + clientHeight >= scrollHeight - Math.max(0, thresholdPx);
            setIsBottomReached((prev) => (prev === reached ? prev : reached));
        };
        const requestCompute = () => {
            if (raf) return;
            raf = window.requestAnimationFrame(() => {
                raf = 0;
                compute();
            });
        };
        compute();
        window.addEventListener('scroll', requestCompute, { passive: true });
        window.addEventListener('resize', requestCompute);
        return () => {
            window.removeEventListener('scroll', requestCompute);
            window.removeEventListener('resize', requestCompute);
            if (raf) window.cancelAnimationFrame(raf);
        };
    }, [thresholdPx]);
    return isBottomReached;
}
